"use client";

import React from "react";
import { useParams } from "next/navigation";
import styles from "./pagination.module.css";

interface PaginationProps {
  page: number;
  total: number;
  onChange: (page: number) => void;
  totalCount?: number;
  visibleCount?: number;
  locale?: string;
}

export default function Pagination({
  page,
  total,
  onChange,
  totalCount,
  visibleCount,
  locale,
}: PaginationProps) {
  const params = useParams();
  const currentLocale = locale || (params?.locale as string) || "vi";
  const isEn = currentLocale === "en";

  // Nếu không có sản phẩm nào và không có trang thì không render
  if (total <= 0 && (totalCount === undefined || totalCount === 0)) {
    return null;
  }

  const pages: (number | "...")[] = [];
  const delta = 2;

  for (let i = 1; i <= total; i++) {
    if (
      i === 1 ||
      i === total ||
      (i >= page - delta && i <= page + delta)
    ) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== "...") {
      pages.push("...");
    }
  }

  return (
    <div className={styles.paginationWrapper}>
      {/* Bên trái: Hiển thị số lượng sản phẩm */}
      {totalCount !== undefined ? (
        <div className={styles.productCount}>
          {isEn ? "Showing " : "Hiển thị "}
          <span className={styles.countHighlight}>
            {visibleCount !== undefined ? visibleCount : totalCount}
          </span>{" "}
          / <span>{totalCount}</span> {isEn ? "products" : "sản phẩm"}
        </div>
      ) : (
        <div />
      )}

      {/* Bên phải: Nút phân trang (chỉ hiện khi có nhiều hơn 1 trang) */}
      {total > 1 ? (
        <nav className={styles.pagination} aria-label="Pagination">
          <button
            className={styles.pageBtn}
            onClick={() => onChange(Math.max(1, page - 1))}
            disabled={page === 1}
            aria-label="Previous"
          >
            ‹
          </button>

          {pages.map((p, i) =>
            p === "..." ? (
              <span key={`dots-${i}`} className={styles.dots}>
                …
              </span>
            ) : (
              <button
                key={p}
                className={`${styles.pageBtn} ${p === page ? styles.active : ""}`}
                onClick={() => onChange(p as number)}
              >
                {p}
              </button>
            )
          )}

          <button
            className={styles.pageBtn}
            onClick={() => onChange(Math.min(total, page + 1))}
            disabled={page === total}
            aria-label="Next"
          >
            ›
          </button>
        </nav>
      ) : null}
    </div>
  );
}
